import { createFeatureSelector, createSelector } from '@ngrx/store';
import { concat } from 'lodash';
import { QTenxAnswerQuestion } from './tenx.interface';
import { QuestionnaireTenxState, questionnaireTenxKey } from './tenx.state';
import { qUtilsTenxIsDateGreaterThanOneMonth } from './tenx.utils';

export const selectQuestionnaireTenx =
  createFeatureSelector<QuestionnaireTenxState>(questionnaireTenxKey);

export const selectQuestionnaireTenxUserProfile = createSelector(
  selectQuestionnaireTenx,
  (state) => state.userProfile
);

export const selectQuestionnaireTenxLoading = createSelector(
  selectQuestionnaireTenx,
  (state: QuestionnaireTenxState) => state.loading
);

export const selectQuestionnaireTenxForm = createSelector(
  selectQuestionnaireTenx,
  (state) => state.form
);

export const selectQuestionnaireTenxFormValidation = createSelector(
  selectQuestionnaireTenx,
  selectQuestionnaireTenxUserProfile,
  (state, uProfile) => {
    const form = state.form;
    const value: Array<boolean> = [];

    // weight
    if (
      uProfile &&
      qUtilsTenxIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.weight ?? ''
      )
    ) {
      if (form.information.weight === null || form.information.weight <= 0) {
        value.push(false);
      }
    }
    // height
    if (
      uProfile &&
      qUtilsTenxIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.height ?? ''
      )
    ) {
      if (form.information.height === null || form.information.height <= 0) {
        value.push(false);
      }
    }
    if (form.medicalHistory.length === 0) {
      value.push(false);
    }
    if (
      uProfile &&
      qUtilsTenxIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.smokingHabits ?? ''
      )
    ) {
      if (form.smokingHabits.smokingStatus?.value !== 'Never') {
        if (
          !form.smokingHabits.startSmoking?.name ||
          !form.smokingHabits.dailyConsumption?.name ||
          (form.smokingHabits.cigarettesType ?? []).length === 0
        ) {
          value.push(false);
        }
      }
    }
    if (
      uProfile &&
      qUtilsTenxIsDateGreaterThanOneMonth(
        uProfile.historicalLastStateUpdate?.alcoholHabits ?? ''
      )
    ) {
      if (form.alcoholHabits.alcoholStatus?.value !== 'No, never') {
        if (
          !form.alcoholHabits.startConsumeAlcohol?.name ||
          !form.alcoholHabits.amountOfAlcohol?.name ||
          (form.alcoholHabits.typeOfAlcohol ?? []).length === 0
        ) {
          value.push(false);
        }
      }
    }

    return value.every((v) => v);
  }
);

export const selectQuestionnaireTenxFormForSubmit = createSelector(
  selectQuestionnaireTenx,
  (state) => {
    const form = state.form;
    const [food, foodOthers] = [
      form.allergies
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          type: 'FOOD',
          name: o.value,
          antidote: null,
          completedName: [o.value, o.description].join(' ').trim()
        })),
      form.allergies
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          type: 'FOOD',
          name: v,
          antidote: null,
          completedName: v
        }))
        .filter(Boolean) ?? []
    ];
    const [conditional, conditionalOthers] = [
      (form.conditionalAllergies ?? [])
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          type: 'CONDITIONAL',
          name: o.value,
          antidote: null,
          completedName: [o.value, o.description].join(' ').trim()
        })),
      (form.conditionalAllergies ?? [])
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          type: 'CONDITIONAL',
          name: v,
          antidote: null,
          completedName: v
        }))
        .filter(Boolean) ?? []
    ];
    const allergies = concat(
      food,
      foodOthers,
      conditional,
      conditionalOthers
    ).filter(Boolean);
    const exerciseLevelPerWeek = form.exerciseLevelPerWeek?.value ?? '';
    const exerciseLevelPerWeekCompletedName = [
      form.exerciseLevelPerWeek?.value,
      form.exerciseLevelPerWeek?.description
    ]
      .join(' ')
      .trim();
    const [medicalHistory, medicalHistoryOthers] = [
      form.medicalHistory
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          medicalId: null,
          parentalType: null as string | null,
          medicalCompletedName: [o.value, o.description].join(' ').trim(),
          medicalNameEn: o.value,
          medicalNameId: o.value,
          medicalType: 'DISEASE',
          medicalIsOther: false
        })),
      form.medicalHistory
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          medicalId: null,
          parentalType: null as string | null,
          medicalCompletedName: v,
          medicalNameEn: v,
          medicalNameId: v,
          medicalType: 'DISEASE',
          medicalIsOther: true
        }))
        .filter(Boolean) ?? []
    ];
    const [fatherMedicalHistory, fatherMedicalHistoryOthers] = [
      (form.fatherMedicalHistory ?? [])
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          medicalId: null,
          parentalType: 'FATHER',
          medicalCompletedName: [o.value, o.description].join(' ').trim(),
          medicalNameEn: o.value,
          medicalNameId: o.value,
          medicalType: 'ILLNESS',
          medicalIsOther: false
        })),
      (form.fatherMedicalHistory ?? [])
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          medicalId: null,
          parentalType: 'FATHER',
          medicalCompletedName: v,
          medicalNameEn: v,
          medicalNameId: v,
          medicalType: 'ILLNESS',
          medicalIsOther: true
        }))
        .filter(Boolean) ?? []
    ];
    const [motherMedicalHistory, motherMedicalHistoryOthers] = [
      (form.motherMedicalHistory ?? [])
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          id: null,
          medicalId: null,
          parentalType: 'MOTHER',
          medicalCompletedName: [o.value, o.description].join(' ').trim(),
          medicalNameEn: o.value,
          medicalNameId: o.value,
          medicalType: 'ILLNESS',
          medicalIsOther: false
        })),
      (form.motherMedicalHistory ?? [])
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          id: null,
          medicalId: null,
          parentalType: 'MOTHER',
          medicalCompletedName: v,
          medicalNameEn: v,
          medicalNameId: v,
          medicalType: 'ILLNESS',
          medicalIsOther: true
        }))
        .filter(Boolean) ?? []
    ];
    const [medications, medicationOthers] = [
      form.medications
        .filter((o) => o.value !== 'OTHER')
        .map((o) => ({
          drug: o.value,
          duration: null
        })),
      form.medications
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => ({
          drug: v,
          duration: null
        }))
        .filter(Boolean) ?? []
    ];
    const sleepHabits = {
      sleepEachNightOnAverage:
        form.sleepHabits.sleepEachNightOnAverage?.value ?? '',
      wakeUpInMiddleSleepEachNight: null
    };
    const [cigarettesType, cigarettesTypeOthers] = [
      (form.smokingHabits.cigarettesType ?? [])
        .filter((o) => o.value !== 'OTHER')
        .map((o) => o.value),
      (form.smokingHabits.cigarettesType ?? [])
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => v)
        .filter(Boolean) ?? []
    ];
    const smokingHabits = {
      smokingStatus: form.smokingHabits.smokingStatus?.value ?? 'Never',
      startSmokingActively: form.smokingHabits.startSmoking?.value ?? null,
      amountInDay: form.smokingHabits.dailyConsumption?.value ?? null,
      type: concat(cigarettesType, cigarettesTypeOthers)
    };
    const [kindOfAlcohol, kindOfAlcoholOthers] = [
      (form.alcoholHabits.typeOfAlcohol ?? [])
        .filter((o) => o.value !== 'OTHER')
        .map((o) => (o.value === 'OTHER' ? o.name : o.value)),
      (form.alcoholHabits.typeOfAlcohol ?? [])
        .find((o) => o.value === 'OTHER')
        ?.name.split(', ')
        .map((v) => v)
        .filter(Boolean) ?? []
    ];
    const alcoholIntake = {
      consumption: form.alcoholHabits.alcoholStatus?.value ?? 'No, never',
      startAlcoholActively:
        form.alcoholHabits.startConsumeAlcohol?.value ?? null,
      amountOfAlcohol: form.alcoholHabits.amountOfAlcohol?.value ?? null,
      kindOfAlcohol: concat(kindOfAlcohol, kindOfAlcoholOthers)
    };
    const vaccinations = form.vaccinations.map((o) =>
      o.value === 'OTHER' ? o.name : o.value
    );
    const value: QTenxAnswerQuestion = {
      information: {
        weight: state.form.information.weight ?? 0,
        height: state.form.information.height ?? 0,
        weighUnit: 'kg',
        heightUnit: 'cm'
      },
      allergies,
      exerciseLevelPerWeek,
      exerciseLevelPerWeekCompletedName,
      medicalHistory: concat(
        medicalHistory,
        medicalHistoryOthers,
        fatherMedicalHistory,
        fatherMedicalHistoryOthers,
        motherMedicalHistory,
        motherMedicalHistoryOthers
      ),
      medications: concat(medications, medicationOthers),
      sleepHabits,
      smokingHabits,
      alcoholIntake,
      vaccinations
    };
    return value;
  }
);

export const selectQuestionnaireTenxSubmissionResponse = createSelector(
  selectQuestionnaireTenx,
  (state) => state.submissionResponse
);
